<template>
  <div class="SignIn" :class="{ 'fill-height': subscriptionBrandingBackgroundUrl }">
    <div v-if="signingFromSubdomain" class="subscription-branding-background"
        :style="{ 'background-image': subscriptionBrandingBackgroundUrl, 'opacity': subscriptionBrandingBackgroundOpacity }">
    </div>
    <div class="SignIn__container px-4" data-locator="sign-in-page">
      <div class="SignIn__topbar">
        <LoginTopbar />
      </div>

      <div class="SignIn__content">
        <div v-if="!signingFromSubdomain && $vuetify.display.mdAndUp">
          <ClosdTagLine />
        </div>

        <div>
          <AppCard class="SignIn__form" data-locator="sign-in-form">
            <div v-if="signingFromSubdomain">
              <v-img :src="subscriptionBranding && subscriptionBranding.logo"
                     width="240"
                     class="mx-auto"
              />
            </div>

            <div class="d-flex flex-column row-gap-8">
              <h2 class="font-weight-medium text-center mt-4">{{ $t('login.views.SignIn.title') }}</h2>

              <v-alert v-if="showCredentialError"
                       density="compact"
                       variant="outlined"
                       type="error"
              >
                {{ $t('login.views.SignIn.wrongCredential') }}
              </v-alert>

              <v-alert v-if="showExpiredError"
                       variant="outlined"
                       border="start"
                       type="error"
              >
                {{ $t('login.views.SignIn.expiredCredential') }}
              </v-alert>

              <v-alert v-if="showOtherSessionStartedMessage"
                       variant="outlined"
                       border="start"
                       type="warning"
              >
                {{ $t('login.views.SignIn.otherSessionStartedInfo') }}
              </v-alert>

              <v-alert v-if="showLogOutMessage && !showVoluntaryLogOutMessage"
                       variant="outlined"
                       border="start"
                       type="warning"
              >
                {{ $t('login.views.SignIn.logOutInfo') }}
              </v-alert>

              <v-alert v-if="isTokenInvalid"
                       variant="outlined"
                       border="start"
                       type="warning"
              >
                {{ $t('login.views.SignIn.InvalidTokenInfo') }}
              </v-alert>

              <AppTextField v-model.trim="login.email"
                            :error-messages="emailErrors"
                            :label="$t('login.views.SignIn.email')"
                            autofocus
                            maxlength="255"
                            type="email"
                            data-locator="mail-input"
                            @blur="validateEmail"
                            @keydown.enter="prepareSignIn"
              />

              <AppTextField v-show="isPasswordFieldVisible"
                            v-model="login.password"
                            :error-messages="passwordErrors"
                            :label="$t('login.views.SignIn.password')"
                            :type="displayPassword ? 'text' : 'password'"
                            data-locator="password-input"
                            autofocus
                            @blur="validatePassword"
                            @keydown.enter="prepareSignIn"
              >
                <template #append-inner>
                  <AppButton icon @click="displayPassword = !displayPassword">
                    <app-material-icon v-if="displayPassword" name="visibility"></app-material-icon>
                    <app-material-icon v-else name="visibility_off"></app-material-icon>
                  </AppButton>
                </template>
              </AppTextField>

              <v-checkbox
                v-model.trim="login.rememberMe"
                :label="$t('login.views.SignIn.rememberMe')"
                class="mt-n2 remember-me-checkbox"
                data-locator="remember-me-checkbox"
                @update:model-value="setRememberMe"
              />

              <AppButton color="primary"
                         class="align-self-center"
                         :data-locator="step === 1 ? 'next-step-button' : 'log-in-button'"
                         :loading="loading || signinPending"
                         @click="prepareSignIn"
              >
                {{ formValidateButtonText }}
              </AppButton>

              <div class="mt-n4 text-center">
                <router-link :to="{ name: FORGOT_PASSWORD_ROUTE_NAME }"
                             class="text-decoration-none text-grey-600"
                             data-locator="forgotten-password-link"
                >
                  {{ $t('login.views.SignIn.forgotPassword') }}
                </router-link>
              </div>

              <div v-if="signingFromSubdomain">
                <v-img src="@/assets/img/closd_logo.svg"
                       width="90"
                       class="mx-auto my-4"
                />
              </div>
            </div>
          </AppCard>

          <div class="text-center mt-6">
            <BackToClosd />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import { formFieldErrors } from '@/common/utils/form'
import { navigatingOnAvailableSubdomain, urlPathJoin } from '@/common/utils/url'
import { Login } from '@/models/login.model'
import { FORGOT_PASSWORD_ROUTE_NAME } from '@/router/routerData'
import { SAML_CHECK, SIGN_IN } from '@/store/modules/login/action_types'
import { SET_STEP } from '@/store/modules/login/mutation_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import BackToClosd from '../BackToClosd.vue'
import ClosdTagLine from '../ClosdTagLine.vue'
import LoginTopbar from '../LoginTopbar.vue'

export default {
  name: 'SignIn',
  components: {
    AppButton,
    AppTextField,
    AppCard,
    ClosdTagLine,
    LoginTopbar,
    BackToClosd,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      login: {
        email: { required, postmarkEmail },
        password: {
          required: requiredIf(() => this.step === 2),
        },
      },
    }
  },
  props: {
    isLogOut: {
      type: [Boolean, String],
      required: false,
    },
    isVoluntaryLogOut: {
      type: [Boolean, String],
      required: false,
    },
    isTokenInvalid: {
      type: Boolean,
      default: false,
    },
    otherSessionStarted: {
      type: [Boolean, String],
      default: false,
    },
    returnRoute: {
      // May be a fullPath string instead of a route object if from query
      type: [Object, String],
      required: false,
    },
    redirectUrl: {
      type: String,
    },
  },
  data () {
    return {
      FORGOT_PASSWORD_ROUTE_NAME,
      displayPassword: false,
      login: new Login(),
      showCredentialError: false,
      showExpiredError: false,
      showLogOutMessage: this.isLogOut && this.isLogOut !== 'false',
      showOtherSessionStartedMessage: this.otherSessionStarted && this.otherSessionStarted !== 'false',
      showVoluntaryLogOutMessage: this.isVoluntaryLogOut && this.isVoluntaryLogOut !== 'false',
      signingFromSubdomain: false,
    }
  },
  computed: {
    ...mapState('login', ['loading', 'signinPending', 'step']),
    ...mapState('user', ['profile']),
    ...mapState('subscription', ['subscriptionBranding']),
    subscriptionBrandingBackgroundUrl () {
      if (!this.subscriptionBranding?.background) return ''
      return `url(${this.subscriptionBranding.background})`
    },
    subscriptionBrandingBackgroundOpacity () {
      if (!this.subscriptionBranding?.backgroundOpacity) return ''
      return `${this.subscriptionBranding.backgroundOpacity}%`
    },
    isPasswordFieldVisible () {
      return !this.signinPending && this.step === 2
    },
    emailErrors () {
      return formFieldErrors(
        this.v$.login.email,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.SignIn.email') })],
          ['postmarkEmail', this.$t('common.validations.email')],
        ],
      )
    },
    passwordErrors () {
      return formFieldErrors(
          this.v$.login.password,
          [
              ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.SignIn.password') })],
          ],
      )
    },
    returnRouteFullPath () {
      if (typeof this.returnRoute === 'object') {
        return this.returnRoute.fullPath
      }

      if (typeof this.returnRoute === 'string') {
        return this.returnRoute
      }

      return null
    },
    formValidateButtonText () {
      return this.step === 1 ? this.$t('login.views.SignIn.next') : this.$t('login.views.SignIn.login')
    },
  },
  mounted () {
    const rememberMeCookie = this.$cookies.get('closd_rememberMe')
    if (rememberMeCookie) {
      this.login.email = rememberMeCookie
      this.login.rememberMe = true
    }
  },
  created () {
    this.FORGOT_PASSWORD_ROUTE_NAME = FORGOT_PASSWORD_ROUTE_NAME
    this.signingFromSubdomain = navigatingOnAvailableSubdomain()
  },
  methods: {
    ...mapActions('login', [SAML_CHECK, SIGN_IN]),
    validateEmail () {
      this.v$.login.email.$touch()
    },
    validatePassword () {
      this.v$.login.password.$touch()
    },
    async prepareSignIn () {
      if (this.loading) {
        return
      }
      try {
        if (this.step === 1) {
          this.validateEmail()
        } else {
          this.validatePassword()
        }

        const isFormValid = await this.v$.login.$validate()
        if (isFormValid) {
          const data = {}
          data.email = this.login.email
          if (this.redirectUrl) {
            data.intendedUrl = this.redirectUrl
          } else if (this.returnRouteFullPath) {
            data.intendedUrl = urlPathJoin(import.meta.env.VUE_APP_CLIENT_URL, this.returnRouteFullPath)
          }

          await this.SAML_CHECK(data)

          if (this.login.rememberMe) {
            this.$cookies.set('closd_rememberMe', this.login.email, '30d')
          }

          if (this.step === 2 && this.v$.login.password.$dirty) {
            await this.SIGN_IN({
              login: this.login,
              redirectRoute: this.returnRouteFullPath,
              redirectUrl: this.redirectUrl,
            })
          }
          this.$store.commit(`login/${SET_STEP}`, 2)
        }
      } catch (e) {
        if (e.response?.status === 401) {
          this.clearError()
          this.showExpiredError = true
        } else {
          this.clearError()
          this.showCredentialError = true
        }
      }
    },
    setRememberMe () {
      if (!this.login.rememberMe) {
        this.$cookies.remove('closd_rememberMe')
      }
    },
    clearError () {
      this.showCredentialError = false
      this.showExpiredError = false
      this.showLogOutMessage = false
      this.showOtherSessionStartedMessage = false
    },
  },
}

</script>

<style scoped lang="scss">
.SignIn {
  position: relative;
  background-color: rgb(var(--v-theme-grey-25));
  height: 100%;

  &__container {
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
    height: 100%;
  }

  &__topbar {
    padding-top: 1rem;
  }

  &__content {
    margin-top: 10%;
    display: flex;
    align-items: center;
    column-gap: 4rem;
    padding: 0;

    & > * {
      flex: 1;
    }
  }

  &__form {
    max-width: 600px;
    min-height: 500px;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__forgot-password-link {
    text-align: center;
  }
}

.subscription-branding-background {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  pointer-events: none;
}
:deep(.v-field.v-field--appended) {
  padding-right: 4px;
}
:deep(.v-text-field .v-input__details) {
  padding-inline: 0px;
}
:deep(.remember-me-checkbox .v-input__details) {
  display: none;
}
.remember-me-checkbox {
  margin-top: 4px !important;
}
</style>
